/* global google */
import React from 'react'
import { inject, observer } from 'mobx-react'
import styled from 'styled-components'

import {
  getGoogleClientId,
  isGoogleIdentityServicesReady,
  renderGoogleButton,
  waitForGoogleIdentityServices,
} from '~/utilities/googleIdentityServices'
import { applyGoogleCredentialToAuthStore } from '~/utilities/googleAuth'

const ButtonSlot = styled.div`
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  min-height: 44px;
  display: flex;
  justify-content: center;
  ${(props) => (props.disabled ? 'opacity: 0.5; pointer-events: none;' : '')}
  /* Let Google Identity Services size its own iframe to the measured slot
     width (passed via renderGoogleButton). Forcing width:100% on the iframe
     shrank it below GIS's rendered content and clipped the button. Slot is
     capped + centered so the button doesn't stretch full-card-width. */
  & > div {
    width: 100% !important;
    max-width: 100% !important;
  }
`

@inject((all) => ({
  auth: all.store.auth,
  isLoggedIn: all.store.auth.isLoggedIn,
  isAttemptingLogIn: all.store.auth.isAttemptingLogIn,
  isAttemptingSignUp: all.store.auth.isAttemptingSignUp,
}))
@observer
class GoogleButton extends React.Component {
  constructor(props) {
    super(props)
    this.buttonSlot = React.createRef()
    this._isMounted = false
    this._renderedWidth = 0
  }

  componentDidMount() {
    this._isMounted = true
    this.setupSignIn()
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.isLoggedIn !== this.props.isLoggedIn ||
      prevProps.isAttemptingLogIn !== this.props.isAttemptingLogIn ||
      prevProps.isAttemptingSignUp !== this.props.isAttemptingSignUp
    ) {
      this.buildSignIn()
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
    }
  }

  handleCredentialResponse = (credential) => {
    applyGoogleCredentialToAuthStore(this.props.auth, credential).then((success) => {
      if (success && this.props.onSuccess) {
        this.props.onSuccess()
      }
    })
  }

  setupSignIn = async () => {
    if (typeof window === 'undefined' || !getGoogleClientId()) return

    const ready = await waitForGoogleIdentityServices()
    if (!this._isMounted || !ready) return

    this.buildSignIn()
    if (this.buttonSlot.current && typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this.buildSignIn())
      this._resizeObserver.observe(this.buttonSlot.current)
    }
  }

  // Kept synchronous: renderGoogleButton wipes and refills the slot we observe,
  // so an await here would let queued resize callbacks slip past the width guard
  // below and start rendering the button on top of itself.
  buildSignIn = () => {
    const slot = this.buttonSlot.current
    if (!this._isMounted || !slot || !isGoogleIdentityServicesReady()) return

    const width = Math.round(slot.offsetWidth)
    // Zero while the slot sits in a modal that has not opened yet; GIS is told
    // to size itself in that case, so wait for a real measurement.
    if (!width) return
    // renderGoogleButton clears the slot and mounts an iframe into it, which
    // resizes the slot and re-fires the observer. Only a width change is a real
    // reason to render again — otherwise each render triggers the next one.
    if (width === this._renderedWidth) return
    this._renderedWidth = width

    renderGoogleButton(slot, {
      onCredential: this.handleCredentialResponse,
    })
  }

  render() {
    const { isLoggedIn, isAttemptingLogIn, isAttemptingSignUp } = this.props

    if (!getGoogleClientId()) return null

    return (
      <ButtonSlot
        disabled={isLoggedIn || isAttemptingLogIn || isAttemptingSignUp}
        ref={this.buttonSlot}
      />
    )
  }
}

GoogleButton.defaultProps = {
  onSuccess: null,
}

export default GoogleButton
